import { useSweepApi } from '../sweep';
import { useCallback } from 'react';
import { ClearbitEnrichment, UserInfo, UserPreferences } from '../../types/UserInfoTypes';
import { AccountOnboardingStep } from '@server/account-onboarding';
import { UserPreferencesTableColumnSettings } from '@server/user-preferences';
import { Permission } from '@server/permissions';

export const useUserApiFacade = () => {
  const sweepApi = useSweepApi();
  return {
    fetch_current_user: useCallback(async () => {
      const response = await sweepApi.get(`/users`);
      return response.data as UserInfo;
    }, [sweepApi]),
    get_permissions_by_crm_org_ids: useCallback(async (): Promise<Record<string, Permission[]>> => {
      const { data } = await sweepApi.get<Record<string, Permission[]>>(
        `/users/permissions-by-crm-orgs`,
      );
      return data;
    }, [sweepApi]),
    delete_user: useCallback(
      async (_id: string) => {
        const response = await sweepApi.delete(`/users/${_id}`);
        return response.data as boolean;
      },
      [sweepApi],
    ),
    delete_invitation: useCallback(
      async (_id: string) => {
        const response = await sweepApi.delete(`/users/account/invitations/${_id}`);
        return response.data as boolean;
      },
      [sweepApi],
    ),
    patch_updateOnboardingStatus: useCallback(
      async (status: AccountOnboardingStep) => {
        const res = await sweepApi.patch('/users/account/onboarding', {
          status,
        });
        return res.data;
      },
      [sweepApi],
    ),
    update_userPreferences: useCallback(
      async (updatedPreferences: UserPreferences) => {
        const { getStarted } = updatedPreferences;
        const preferencesForApi = {
          ...updatedPreferences,
          getStarted,
        };
        const res = await sweepApi.put('/users/preferences', preferencesForApi);
        return res.data;
      },
      [sweepApi],
    ),
    patch_userPreferences: useCallback(
      async (updatedPreferences: Partial<UserPreferences>) => {
        const res = await sweepApi.patch<UserPreferencesTableColumnSettings[]>(
          '/users/preferences',
          updatedPreferences,
        );
        return res.data;
      },
      [sweepApi],
    ),
    post_accountTrial: useCallback(async () => {
      await sweepApi.post('/users/account/trial');
    }, [sweepApi]),

    get_clearbitEnrichment: useCallback(async () => {
      const res = await sweepApi.get('/users/clearbit-enrichment');
      return res.data as ClearbitEnrichment | undefined;
    }, [sweepApi]),
    post_hubspotEnrichment: useCallback(
      async (payload: Record<string, any>) => {
        await sweepApi.post('/users/hubspot-enrichment', payload);
      },
      [sweepApi],
    ),
    set_mcp_org_for_user: useCallback(
      async (orgId: string) => {
        await sweepApi.post(`/users/${orgId}/mcp-org`);
      },
      [sweepApi],
    ),
  };
};
